import { VITE_FORGE_URL } from '@constants';
import { DesignSystem, Profile, Scene } from 'dragonmouse-shared/interfaces';
import {
	refreshFramePermissions,
	resolveOptimisticallyUpdatedScenes,
	resolveOptimisticallyUpdatedUser,
	setLoadingState,
	updateCreditWallet,
	updateDesignSystems,
	updateOrganizationCreditWallets,
	updateOrganizationDesignSystemIds,
	updateOrganizations,
	updateOrganizationSceneIds,
	updateScenes,
	updateUser,
	updateUserDesignSystemIds,
	updateUserSceneIds,
} from 'redux/db/actions';
import store from 'redux/store';
import { PollingStream } from './PollingStream';

export class ProfilePollingStream extends PollingStream<Profile> {
	constructor() {
		const url = VITE_FORGE_URL + '/poll/profile';

		const onError = (error: Error) => {
			console.error('Profile polling stream error:', error);
			this.onData([]);
		};

		super(url, onError, 'profile');
	}

	extractData(response: any): Profile[] {
		// Profile endpoint returns a single profile object or null
		if (response === null || response === undefined) {
			return [];
		}
		return [response];
	}

	onData(profiles: Profile[]) {
		if (profiles.length === 0 || !profiles[0]) {
			store.dispatch(updateScenes([]));
			store.dispatch(setLoadingState({ resource: 'profile', state: 'loaded' }));
			store.dispatch(setLoadingState({ resource: 'scenes', state: 'loaded' }));
			return;
		}

		const profile = profiles[0];
		let scenes: Scene[] = [];
		let designSystems: DesignSystem[] = [];

		if (profile.scenes.length > 0) {
			// update scenes
			scenes = profile.scenes.map((sceneRow) => ({
				id: sceneRow.id,
				title: sceneRow.title,
				metadata: sceneRow.metadata,
				createdAt: new Date(sceneRow.createdAt),
				updatedAt: sceneRow.updatedAt ? new Date(sceneRow.updatedAt) : undefined,
				writeId: sceneRow.writeId,
				viewOnly: sceneRow.viewOnly,
			}));

			// resolve optimistically updated scenes
			if (profile.writeIds.length > 0) {
				store.dispatch(resolveOptimisticallyUpdatedScenes(profile.writeIds));
			}
		}

		if (profile.designSystems && profile.designSystems.length > 0) {
			// update design systems
			designSystems = profile.designSystems.map((dsRow) => ({
				id: dsRow.id,
				title: dsRow.title,
				writeId: dsRow.writeId,
				metadata: dsRow.metadata || {},
				createdAt: new Date(dsRow.createdAt),
				updatedAt: new Date(dsRow.updatedAt),
			}));
		}

		store.dispatch(updateScenes(scenes));
		store.dispatch(updateDesignSystems(designSystems));
		store.dispatch(updateOrganizations(profile.organizations ?? []));
		store.dispatch(updateOrganizationSceneIds(profile.organizationSceneIds ?? {}));
		store.dispatch(updateOrganizationDesignSystemIds(profile.organizationDesignSystemIds ?? {}));
		store.dispatch(updateUserSceneIds(profile.userSceneIds ?? []));
		store.dispatch(updateUserDesignSystemIds(profile.userDesignSystemIds ?? []));
		store.dispatch(setLoadingState({ resource: 'scenes', state: 'loaded' }));

		// Refresh frame permissions now that scenes are loaded
		// This will update viewOnly flags on existing frames based on the new scenes collection
		store.dispatch(refreshFramePermissions());

		// Update user in Redux
		if (profile.writeIds.length > 0) {
			store.dispatch(resolveOptimisticallyUpdatedUser(profile.writeIds));
			// @todo grant/revoke access to a scene doesn't use writeIds
		}

		store.dispatch(updateUser(profile.user));
		store.dispatch(updateCreditWallet(profile.creditWallet ?? null));
		store.dispatch(updateOrganizationCreditWallets(profile.organizationCreditWallets ?? {}));
		store.dispatch(setLoadingState({ resource: 'profile', state: 'loaded' }));
	}
}
